import { chartNum } from '@/lib/recharts-format';
import React, { useState, useEffect, useCallback } from 'react';
// Task #1237 — Sezione quota DB
import TenantDBQuotaSection from '@/components/storage/TenantDBQuotaSection';
import { useParams, useNavigate } from 'react-router-dom';
import { GlassCard, GlassCardContent, GlassCardDescription, GlassCardHeader, GlassCardTitle } from '@easyflow/ui-master/components/ui/glass-card';
import { Button } from '@easyflow/ui-master/components/ui/button';
import { Badge } from '@easyflow/ui-master/components/ui/badge';
import { Input } from '@easyflow/ui-master/components/ui/input';
import { Label } from '@easyflow/ui-master/components/ui/label';
import { Switch } from '@easyflow/ui-master/components/ui/switch';
import { Progress } from '@easyflow/ui-master/components/ui/progress';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@easyflow/ui-master/components/ui/dialog';
import {
  PieChart,
  Pie,
  Cell,
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import {
  ArrowLeft,
  HardDrive,
  Mic,
  FileText,
  Video,
  RefreshCw,
  Trash2,
  Calculator,
  AlertTriangle,
  CheckCircle,
  XCircle,
  Clock,
  CloudOff,
  Loader2,
  Save,
} from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import apiClient from '@/lib/api';

const COLORS = ['#3b82f6', '#8b5cf6', '#ec4899', '#10b981'];

interface TenantStorageData {
  tenantId: string;
  tenantName: string;
  tenantSlug: string;
  quota: {
    maxBytes: number;
    usedBytes: number;
    percentage: number;
    recordings: { enabled: boolean; maxBytes: number; usedBytes: number; retentionDays: number };
    videos: { usedBytes: number };
    documents: { usedBytes: number };
  };
  sync: {
    lastSyncAt: string | null;
    lastSyncFilesCount: number;
    lastSyncBytes: number;
    syncErrors24h: number;
    pendingFiles: number;
    pendingBytes: number;
  };
  recentFiles: Array<{
    id: string;
    fileName: string;
    fileSize: number;
    fileSizeFormatted: string;
    category: string;
    cloudSynced: boolean;
    createdAt: string;
  }>;
  alerts: Array<{
    id: string;
    alertType: string;
    severity: string;
    title: string;
    details: Record<string, unknown> | null;
    resolved: boolean;
    resolvedAt: string | null;
    createdAt: string;
  }>;
}

interface QuotaForm {
  maxBytes: number;
  recordingsEnabled: boolean;
  recordingsMaxBytes: number;
  recordingsRetentionDays: number;
}

function formatBytes(bytes: number): string {
  if (bytes === 0) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB', 'TB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
}

function bytesToGB(bytes: number): number {
  return parseFloat((bytes / (1024 * 1024 * 1024)).toFixed(2));
}

function gbToBytes(gb: number): number {
  return Math.round(gb * 1024 * 1024 * 1024);
}

export default function StorageTenantDetail() {
  const { tenantId } = useParams<{ tenantId: string }>();
  const navigate = useNavigate();
  const { toast } = useToast();

  const [isLoading, setIsLoading] = useState(true);
  const [data, setData] = useState<TenantStorageData | null>(null);
  const [quotaDialogOpen, setQuotaDialogOpen] = useState(false);
  const [quotaForm, setQuotaForm] = useState<QuotaForm>({
    maxBytes: 0,
    recordingsEnabled: true,
    recordingsMaxBytes: 0,
    recordingsRetentionDays: 90,
  });
  const [isSaving, setIsSaving] = useState(false);
  const [actionLoading, setActionLoading] = useState<string | null>(null);

  const loadData = useCallback(async () => {
    if (!tenantId) return;
    try {
      setIsLoading(true);
      const response = await apiClient.get<TenantStorageData>(`/master/storage/tenants/${tenantId}`);
      if (response.success && response.data) {
        setData(response.data);
        setQuotaForm({
          maxBytes: response.data.quota.maxBytes,
          recordingsEnabled: response.data.quota.recordings.enabled,
          recordingsMaxBytes: response.data.quota.recordings.maxBytes,
          recordingsRetentionDays: response.data.quota.recordings.retentionDays,
        });
      }
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to load tenant storage data';
      toast({ title: 'Error', description: message, variant: 'destructive' });
    } finally {
      setIsLoading(false);
    }
  }, [tenantId, toast]);

  useEffect(() => {
    void loadData();
  }, [loadData]);

  const saveQuota = async () => {
    if (!tenantId) return;
    try {
      setIsSaving(true);
      await apiClient.put(`/master/storage/tenants/${tenantId}/quota`, {
        maxBytes: quotaForm.maxBytes,
        recordingsEnabled: quotaForm.recordingsEnabled,
        recordingsMaxBytes: quotaForm.recordingsMaxBytes,
        recordingsRetentionDays: quotaForm.recordingsRetentionDays,
      });
      toast({ title: 'Quota aggiornata', description: 'Le quote sono state salvate con successo' });
      setQuotaDialogOpen(false);
      void loadData();
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to update quota';
      toast({ title: 'Error', description: message, variant: 'destructive' });
    } finally {
      setIsSaving(false);
    }
  };

  const executeAction = async (action: string, label: string) => {
    if (!tenantId) return;
    try {
      setActionLoading(action);
      await apiClient.post(`/master/storage/tenants/${tenantId}/${action}`);
      toast({ title: 'Azione completata', description: `${label} completato con successo` });
      void loadData();
    } catch (error) {
      const message = error instanceof Error ? error.message : `Failed to execute ${label}`;
      toast({ title: 'Error', description: message, variant: 'destructive' });
    } finally {
      setActionLoading(null);
    }
  };

  if (isLoading || !data) {
    return (
      <div className="space-y-6 p-6">
        <div className="flex items-center gap-4">
          <div className="h-8 w-8 bg-white/20 rounded animate-pulse" />
          <div className="h-8 w-64 bg-white/20 rounded animate-pulse" />
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {[1, 2, 3, 4].map(i => (
            <div key={i} className="h-32 bg-white/10 rounded-lg animate-pulse" />
          ))}
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="h-80 bg-white/10 rounded-lg animate-pulse" />
          <div className="h-80 bg-white/10 rounded-lg animate-pulse" />
        </div>
      </div>
    );
  }

  const breakdownData = [
    { name: 'Recordings', value: data.quota.recordings.usedBytes },
    { name: 'Videos', value: data.quota.videos.usedBytes },
    { name: 'Documents', value: data.quota.documents.usedBytes },
  ].filter(d => d.value > 0);

  const trendData = data.alerts
    .filter(a => a.createdAt)
    .slice(0, 30)
    .reverse()
    .map(a => ({
      date: new Date(a.createdAt).toLocaleDateString([], { month: 'short', day: 'numeric' }),
      alerts: 1,
    }));

  const aggregatedTrend = trendData.reduce((acc: Array<{ date: string; alerts: number }>, item) => {
    const existing = acc.find(a => a.date === item.date);
    if (existing) {
      existing.alerts += 1;
    } else {
      acc.push({ ...item });
    }
    return acc;
  }, []);

  const severityColor = (severity: string) => {
    switch (severity) {
      case 'critical': return 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400';
      case 'warning': return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400';
      case 'info': return 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400';
      default: return 'bg-muted text-foreground dark:bg-gray-900/30 dark:text-muted-foreground/70';
    }
  };

  const categoryIcon = (category: string) => {
    switch (category) {
      case 'recording': return <Mic className="h-4 w-4" />;
      case 'video': return <Video className="h-4 w-4" />;
      default: return <FileText className="h-4 w-4" />;
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="sm" onClick={() => navigate('/storage')}>
            <ArrowLeft className="h-4 w-4 mr-2" />
            Torna a Tenants
          </Button>
          <div>
            <h1 className="text-2xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
              {data.tenantName}
            </h1>
            <p className="text-sm text-muted-foreground dark:text-muted-foreground/70">{data.tenantSlug}</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={() => loadData()}>
            <RefreshCw className="h-4 w-4 mr-2" />
            Refresh
          </Button>
          <Button size="sm" onClick={() => setQuotaDialogOpen(true)}>
            <Save className="h-4 w-4 mr-2" />
            Modifica Quote
          </Button>
        </div>
      </div>

      {/* Task #1237 — Sezione quota DB tenant */}
      {tenantId && <TenantDBQuotaSection tenantId={tenantId} />}

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <GlassCard className="border-l-4 border-l-blue-500">
          <GlassCardHeader className="pb-2">
            <GlassCardDescription className="flex items-center justify-between">
              <span>Spazio Globale</span>
              <HardDrive className="h-4 w-4 text-blue-600" />
            </GlassCardDescription>
          </GlassCardHeader>
          <GlassCardContent>
            <div className="text-2xl font-bold">{formatBytes(data.quota.usedBytes)}</div>
            <div className="text-sm text-muted-foreground">di {formatBytes(data.quota.maxBytes)}</div>
            <Progress value={data.quota.percentage} className="mt-2 h-2" />
            <div className="text-xs text-muted-foreground/70 mt-1">{data.quota.percentage}%</div>
          </GlassCardContent>
        </GlassCard>

        <GlassCard className="border-l-4 border-l-purple-500">
          <GlassCardHeader className="pb-2">
            <GlassCardDescription className="flex items-center justify-between">
              <span>Registrazioni</span>
              <Mic className="h-4 w-4 text-purple-600" />
            </GlassCardDescription>
          </GlassCardHeader>
          <GlassCardContent>
            <div className="text-2xl font-bold">{formatBytes(data.quota.recordings.usedBytes)}</div>
            {data.quota.recordings.maxBytes > 0 && (
              <>
                <div className="text-sm text-muted-foreground">di {formatBytes(data.quota.recordings.maxBytes)}</div>
                <Progress
                  value={data.quota.recordings.maxBytes > 0 ? (data.quota.recordings.usedBytes / data.quota.recordings.maxBytes) * 100 : 0}
                  className="mt-2 h-2"
                />
              </>
            )}
            <Badge variant={data.quota.recordings.enabled ? 'default' : 'secondary'} className="mt-2">
              {data.quota.recordings.enabled ? 'Abilitato' : 'Disabilitato'}
            </Badge>
          </GlassCardContent>
        </GlassCard>

        <GlassCard className="border-l-4 border-l-orange-500">
          <GlassCardHeader className="pb-2">
            <GlassCardDescription className="flex items-center justify-between">
              <span>Video</span>
              <Video className="h-4 w-4 text-orange-600" />
            </GlassCardDescription>
          </GlassCardHeader>
          <GlassCardContent>
            <div className="text-2xl font-bold">{formatBytes(data.quota.videos.usedBytes)}</div>
            <div className="text-sm text-muted-foreground">
              + {formatBytes(data.quota.documents.usedBytes)} documenti
            </div>
          </GlassCardContent>
        </GlassCard>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <GlassCard>
          <GlassCardHeader>
            <GlassCardTitle>Breakdown Storage</GlassCardTitle>
            <GlassCardDescription>Distribuzione per categoria</GlassCardDescription>
          </GlassCardHeader>
          <GlassCardContent>
            {breakdownData.length > 0 ? (
              <ResponsiveContainer width="100%" height={280}>
                <PieChart>
                  <Pie
                    data={breakdownData}
                    cx="50%"
                    cy="50%"
                    labelLine
                    label={(props: { name?: string; value?: number }) => `${props.name}: ${formatBytes(Number(props.value) || 0)}`}
                    outerRadius={90}
                    fill="#8884d8"
                    dataKey="value"
                  >
                    {breakdownData.map((_entry, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip
                    formatter={(value) => formatBytes(chartNum(value))}
                    contentStyle={{
                      backgroundColor: 'rgba(15, 23, 42, 0.9)',
                      border: '1px solid rgba(255,255,255,0.1)',
                      borderRadius: '8px',
                      color: '#fff'
                    }}
                  />
                  <Legend />
                </PieChart>
              </ResponsiveContainer>
            ) : (
              <div className="flex items-center justify-center h-[280px] text-muted-foreground">
                Nessun dato disponibile
              </div>
            )}
          </GlassCardContent>
        </GlassCard>

        <GlassCard>
          <GlassCardHeader>
            <GlassCardTitle>Alert Trend</GlassCardTitle>
            <GlassCardDescription>Alert negli ultimi 30 giorni</GlassCardDescription>
          </GlassCardHeader>
          <GlassCardContent>
            {aggregatedTrend.length > 0 ? (
              <ResponsiveContainer width="100%" height={280}>
                <AreaChart data={aggregatedTrend}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="date" />
                  <YAxis />
                  <Tooltip />
                  <Area type="monotone" dataKey="alerts" stroke="#ef4444" fill="#ef4444" fillOpacity={0.2} />
                </AreaChart>
              </ResponsiveContainer>
            ) : (
              <div className="flex items-center justify-center h-[280px] text-muted-foreground">
                Nessun alert nel periodo
              </div>
            )}
          </GlassCardContent>
        </GlassCard>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <GlassCard>
          <GlassCardHeader>
            <GlassCardTitle>Stato Sync</GlassCardTitle>
            <GlassCardDescription>Sincronizzazione cloud R2</GlassCardDescription>
          </GlassCardHeader>
          <GlassCardContent>
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <div className="text-sm text-muted-foreground">Ultimo Sync</div>
                  <div className="font-medium">
                    {data.sync.lastSyncAt
                      ? new Date(data.sync.lastSyncAt).toLocaleString()
                      : 'Mai'}
                  </div>
                </div>
                <div>
                  <div className="text-sm text-muted-foreground">File in attesa</div>
                  <div className="font-medium flex items-center gap-2">
                    {data.sync.pendingFiles > 0 ? (
                      <>
                        <Clock className="h-4 w-4 text-yellow-500" />
                        {data.sync.pendingFiles} ({formatBytes(data.sync.pendingBytes)})
                      </>
                    ) : (
                      <>
                        <CheckCircle className="h-4 w-4 text-green-500" />
                        Tutto sincronizzato
                      </>
                    )}
                  </div>
                </div>
                <div>
                  <div className="text-sm text-muted-foreground">Errori 24h</div>
                  <div className="font-medium flex items-center gap-2">
                    {data.sync.syncErrors24h > 0 ? (
                      <>
                        <XCircle className="h-4 w-4 text-red-500" />
                        {data.sync.syncErrors24h}
                      </>
                    ) : (
                      <>
                        <CheckCircle className="h-4 w-4 text-green-500" />
                        0
                      </>
                    )}
                  </div>
                </div>
                <div>
                  <div className="text-sm text-muted-foreground">Ultimo batch</div>
                  <div className="font-medium">
                    {data.sync.lastSyncFilesCount || 0} file ({formatBytes(data.sync.lastSyncBytes)})
                  </div>
                </div>
              </div>
              <div className="flex gap-2 pt-2 border-t">
                <Button
                  variant="outline"
                  size="sm"
                  disabled={actionLoading !== null}
                  onClick={() => executeAction('force-sync', 'Force Sync')}
                >
                  {actionLoading === 'force-sync' ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <RefreshCw className="h-4 w-4 mr-2" />}
                  Forza Sync
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  disabled={actionLoading !== null}
                  onClick={() => executeAction('force-cleanup', 'Cleanup')}
                >
                  {actionLoading === 'force-cleanup' ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Trash2 className="h-4 w-4 mr-2" />}
                  Cleanup
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  disabled={actionLoading !== null}
                  onClick={() => executeAction('recalculate', 'Ricalcolo')}
                >
                  {actionLoading === 'recalculate' ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Calculator className="h-4 w-4 mr-2" />}
                  Ricalcola
                </Button>
              </div>
            </div>
          </GlassCardContent>
        </GlassCard>

        <GlassCard>
          <GlassCardHeader>
            <GlassCardTitle>Configurazione Quote</GlassCardTitle>
            <GlassCardDescription>Quote attuali del tenant</GlassCardDescription>
          </GlassCardHeader>
          <GlassCardContent>
            <div className="space-y-3">
              <div className="flex justify-between items-center py-2 border-b">
                <span className="text-sm text-muted-foreground dark:text-muted-foreground/70">Quota Globale</span>
                <span className="font-medium">{formatBytes(data.quota.maxBytes)}</span>
              </div>
              <div className="flex justify-between items-center py-2 border-b">
                <span className="text-sm text-muted-foreground dark:text-muted-foreground/70">Registrazioni</span>
                <div className="flex items-center gap-2">
                  <Badge variant={data.quota.recordings.enabled ? 'default' : 'secondary'}>
                    {data.quota.recordings.enabled ? 'ON' : 'OFF'}
                  </Badge>
                  <span className="font-medium">{formatBytes(data.quota.recordings.maxBytes)}</span>
                </div>
              </div>
              <div className="flex justify-between items-center py-2 border-b">
                <span className="text-sm text-muted-foreground dark:text-muted-foreground/70">Retention Registrazioni</span>
                <span className="font-medium">{data.quota.recordings.retentionDays} giorni</span>
              </div>
              <Button size="sm" className="w-full mt-2" onClick={() => setQuotaDialogOpen(true)}>
                Modifica Quote
              </Button>
            </div>
          </GlassCardContent>
        </GlassCard>
      </div>

      <GlassCard>
        <GlassCardHeader>
          <GlassCardTitle>Storico Alert</GlassCardTitle>
          <GlassCardDescription>Ultimi 30 giorni per questo tenant</GlassCardDescription>
        </GlassCardHeader>
        <GlassCardContent>
          {data.alerts.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b">
                    <th className="text-left p-3 text-sm font-medium">Data</th>
                    <th className="text-left p-3 text-sm font-medium">Tipo</th>
                    <th className="text-left p-3 text-sm font-medium">Severity</th>
                    <th className="text-left p-3 text-sm font-medium">Titolo</th>
                    <th className="text-left p-3 text-sm font-medium">Stato</th>
                  </tr>
                </thead>
                <tbody>
                  {data.alerts.map(alert => (
                    <tr key={alert.id} className="border-b hover:bg-muted/50 dark:hover:bg-white/5">
                      <td className="p-3 text-sm">
                        {new Date(alert.createdAt).toLocaleDateString()} {new Date(alert.createdAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                      </td>
                      <td className="p-3 text-sm">{alert.alertType}</td>
                      <td className="p-3">
                        <Badge className={severityColor(alert.severity)}>{alert.severity}</Badge>
                      </td>
                      <td className="p-3 text-sm">{alert.title}</td>
                      <td className="p-3">
                        {alert.resolved ? (
                          <Badge variant="outline" className="text-green-600">
                            <CheckCircle className="h-3 w-3 mr-1" />
                            Risolto
                          </Badge>
                        ) : (
                          <Badge variant="outline" className="text-yellow-600">
                            <AlertTriangle className="h-3 w-3 mr-1" />
                            Attivo
                          </Badge>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="text-center py-8 text-muted-foreground">
              <CheckCircle className="h-8 w-8 mx-auto mb-2 text-green-500" />
              Nessun alert per questo tenant
            </div>
          )}
        </GlassCardContent>
      </GlassCard>

      <GlassCard>
        <GlassCardHeader>
          <GlassCardTitle>Ultimi File</GlassCardTitle>
          <GlassCardDescription>File pi recenti caricati</GlassCardDescription>
        </GlassCardHeader>
        <GlassCardContent>
          {data.recentFiles.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b">
                    <th className="text-left p-3 text-sm font-medium">Nome</th>
                    <th className="text-left p-3 text-sm font-medium">Categoria</th>
                    <th className="text-right p-3 text-sm font-medium">Dimensione</th>
                    <th className="text-left p-3 text-sm font-medium">Sync</th>
                    <th className="text-left p-3 text-sm font-medium">Data</th>
                  </tr>
                </thead>
                <tbody>
                  {data.recentFiles.map(file => (
                    <tr key={file.id} className="border-b hover:bg-muted/50 dark:hover:bg-white/5">
                      <td className="p-3 text-sm font-medium flex items-center gap-2">
                        {categoryIcon(file.category)}
                        <span className="truncate max-w-[250px]">{file.fileName}</span>
                      </td>
                      <td className="p-3 text-sm capitalize">{file.category}</td>
                      <td className="p-3 text-sm text-right">{file.fileSizeFormatted}</td>
                      <td className="p-3">
                        {file.cloudSynced ? (
                          <Badge variant="outline" className="text-green-600">
                            <CheckCircle className="h-3 w-3 mr-1" />
                            Synced
                          </Badge>
                        ) : (
                          <Badge variant="outline" className="text-yellow-600">
                            <CloudOff className="h-3 w-3 mr-1" />
                            Pending
                          </Badge>
                        )}
                      </td>
                      <td className="p-3 text-sm">
                        {new Date(file.createdAt).toLocaleDateString()} {new Date(file.createdAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="text-center py-8 text-muted-foreground">
              Nessun file trovato
            </div>
          )}
        </GlassCardContent>
      </GlassCard>

      <Dialog open={quotaDialogOpen} onOpenChange={setQuotaDialogOpen}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle>Modifica Quote — {data.tenantName}</DialogTitle>
            <DialogDescription>Configura le quote storage per questo tenant</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Quota Globale (GB)</Label>
              <Input
                type="number"
                step="0.1"
                min="0"
                value={bytesToGB(quotaForm.maxBytes)}
                onChange={e => setQuotaForm(f => ({ ...f, maxBytes: gbToBytes(parseFloat(e.target.value) || 0) }))}
              />
            </div>

            <div className="border-t pt-4">
              <div className="flex items-center justify-between mb-2">
                <Label>Cloud Recording</Label>
                <Switch
                  checked={quotaForm.recordingsEnabled}
                  onCheckedChange={v => setQuotaForm(f => ({ ...f, recordingsEnabled: v }))}
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-xs">Max (GB)</Label>
                  <Input
                    type="number"
                    step="0.1"
                    min="0"
                    value={bytesToGB(quotaForm.recordingsMaxBytes)}
                    onChange={e => setQuotaForm(f => ({ ...f, recordingsMaxBytes: gbToBytes(parseFloat(e.target.value) || 0) }))}
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-xs">Retention (giorni)</Label>
                  <Input
                    type="number"
                    min="1"
                    value={quotaForm.recordingsRetentionDays}
                    onChange={e => setQuotaForm(f => ({ ...f, recordingsRetentionDays: parseInt(e.target.value) || 90 }))}
                  />
                </div>
              </div>
            </div>

          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setQuotaDialogOpen(false)}>Annulla</Button>
            <Button onClick={saveQuota} disabled={isSaving}>
              {isSaving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Salva
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
